import { Column } from "@albertsons/uds/molecule/Table/Table.types";
import { Tooltip } from "@albertsons/uds/molecule/Tooltip";
import clsx from "clsx";
import SkeletonLoader from "@albertsons/uds/molecule/SkeletonLoader";
import { X as CloseIcon, Info } from "lucide-react";
import { transformQuantity } from "./formatNumber";
import AboutLinkTable from "../pages/about/AboutLinkTable";
import { TableSchema } from "../pages/about/utils";
import { TabHeader } from "../components";
import Modal from "@albertsons/uds/molecule/Modal";
import { unitTabs } from "../constants/units";
import {
  IData,
  IUnits,
  IUnitsData,
  IUnitsTotalData,
  UnitTab,
} from "../types/data.types";

const getUnitInfoContent = (
  kpiDefinition: TableSchema[],
  reportConsiderations: string[],
) => (
  <div className="p-2 min-w-[300px] max-w-[600px]">
    <AboutLinkTable
      isSmall
      tableDataList={kpiDefinition}
      tableTitle="KPI Definition"
      tableHeaderList={Object.keys(kpiDefinition[0])}
    />

    <TabHeader
      title="Report Considerations"
      className="!px-2 py-1 border-zinc-400 text-sm"
    />

    <div className="w-full px-2 py-1">
      {reportConsiderations.map((consideration: string) => (
        <div className="flex justify-start items-center w-full bg-gray-300 px-2 py-2 border border-white hover:bg-gray-400 text-xs">
          {consideration}
        </div>
      ))}
    </div>
  </div>
);

export const getDesktopTableColumns = (
  isUnified: boolean,
  isDsd: boolean,
  kpiDefinition: TableSchema[],
  reportConsiderations: string[],
): Column<IData<number>>[] => [
  {
    id: "label",
    label: (
      <div className="flex justify-center items-center font-normal text-left">
        <Tooltip zIndex={20} anchor="right" overflow>
          <Tooltip.Popover>
            {getUnitInfoContent(kpiDefinition, reportConsiderations)}
          </Tooltip.Popover>
          <Info size={16} data-testid="unitInfo" />
        </Tooltip>
      </div>
    ),
    value: (row: IData<number>) => (
      <div
        className="px-3 text-sm text-white select-none truncate text-center"
        title={`${row.label}`}
      >
        {row.label}
      </div>
    ),
  },
  {
    id: "storeInventory",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate text-center"
        title="Store Inventory"
      >
        Store Inventory
      </div>
    ),
    value: (row: IData<number>) => {
      if ("storeInventory" in row || row.isLoading === 0) {
        return (
          <span className="w-full text-base px-3 flex items-center justify-center">
            {row.storeInventory !== undefined
              ? transformQuantity(row.storeInventory, 1)
              : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
  {
    id: "inTransitToStore",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate text-center"
        title="In Transit to Store"
      >
        In Transit to Store
      </div>
    ),
    value: (row: IData<number>) => {
      if ("inTransitToStore" in row || row.isLoading === 0) {
        return (
          <span className="w-full text-base px-3 flex items-center justify-center">
            {row.inTransitToStore !== undefined
              ? transformQuantity(row.inTransitToStore, 1)
              : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
  ...(isUnified || isDsd
    ? [
        {
          id: "dsdOnOrder",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate text-center"
              title="DSD Order"
            >
              DSD Order
            </div>
          ),
          value: (row: IData<number>) => {
            if ("dsdOnOrder" in row || row.isLoading === 0) {
              return (
                <span className="w-full text-base px-3 flex items-center justify-center">
                  {row.dsdOnOrder !== undefined
                    ? transformQuantity(row.dsdOnOrder, 1)
                    : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
      ]
    : []),
  ...(isUnified || !isDsd
    ? [
        {
          id: "dcInventory",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate text-center"
              title="DC Inventory"
            >
              DC Inventory
            </div>
          ),
          value: (row: IData<number>) => {
            if ("dcInventory" in row || row.isLoading === 0) {
              return (
                <span className="w-full text-base px-3 flex items-center justify-center">
                  {row.dcInventory !== undefined
                    ? transformQuantity(row.dcInventory, 1)
                    : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
        {
          id: "inTransitToDC",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate text-center"
              title="In Transit to DC"
            >
              In Transit to DC
            </div>
          ),
          value: (row: IData<number>) => {
            if ("inTransitToDC" in row || row.isLoading === 0) {
              return (
                <span className="w-full text-base px-3 flex items-center justify-center">
                  {row.inTransitToDC !== undefined
                    ? transformQuantity(row.inTransitToDC, 1)
                    : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
        {
          id: "onOrder",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate text-center"
              title="Vendor On Order (WHS)"
            >
              Vendor On Order (WHS)
            </div>
          ),
          value: (row: IData<number>) => {
            if ("onOrder" in row || row.isLoading === 0) {
              return (
                <span className="w-full text-base px-3 flex items-center justify-center">
                  {row.onOrder !== undefined
                    ? transformQuantity(row.onOrder, 1)
                    : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
      ]
    : []),
  {
    id: "total",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate text-center"
        title="Total"
      >
        Total
      </div>
    ),
    value: (row: IData<number>) => {
      if (row.isLoading === 0) {
        return (
          <span className="w-full text-base font-semibold px-3 flex items-center justify-center">
            {row.total !== undefined ? transformQuantity(row.total, 1) : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
];

interface UnitInfoModalProps {
  isOpen: boolean;
  kpiDefinition: TableSchema[];
  reportConsiderations: string[];
  closeHandler: () => void;
}

export const UnitInfoModal = ({
  isOpen,
  kpiDefinition,
  reportConsiderations,
  closeHandler,
}: UnitInfoModalProps) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={closeHandler}
      minHeight={20}
      minWidth={20}
      width={600}
      maxWidth="calc(100vw - 40px)"
      className="bg-white overflow-hidden !rounded-lg"
    >
      {({ close }) => (
        <div data-testid="unit-info-modal">
          <div className="flex h-12 p-2">
            <div className="flex text-center items-center justify-center font-bold w-full px-3">
              <p>Units Information</p>
              <span
                className="ml-auto flex text-center items-center justify-center cursor-pointer"
                data-testid="report-issue-close-button"
                onClick={close}
              >
                <CloseIcon />
              </span>
            </div>
          </div>
          {getUnitInfoContent(kpiDefinition, reportConsiderations)}
        </div>
      )}
    </Modal>
  );
};

export const getMobileTableColumns = (
  label: string,
  isExtraSmall: boolean,
  loading: number,
  isRetailOps: boolean,
  openUnitInfoModal: () => void,
): Column<IData>[] => [
  {
    id: "label",
    label: (
      <div className="flex justify-center items-center font-normal text-left">
        <Info size={16} data-testid="unitInfo" onClick={openUnitInfoModal} />
      </div>
    ),
    value: (row: IData) => (
      <span
        className={clsx(
          "w-full px-2 text-white flex items-center justify-center",
          isExtraSmall ? "text-sm" : "text-base",
          row.className ?? "",
        )}
      >
        {row.label}
      </span>
    ),
  },
  {
    id: "units",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate flex items-center justify-center"
        title={label}
      >
        {label}
      </div>
    ),
    value: (row: IData) => {
      if (row.units || loading === 0) {
        return (
          <span
            className={clsx(
              "px-2 truncate flex items-center justify-center",
              isExtraSmall ? "text-sm" : "text-base",
              row.className ?? "",
            )}
          >
            {row.units !== undefined ? transformQuantity(row.units, 1) : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
  ...(isRetailOps
    ? [
        {
          id: "avgWeeklyMovement",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate flex items-center justify-center"
              title="AWM"
            >
              AWM
            </div>
          ),
          value: (row: IData) => {
            if (row.avgWeeklyMovement || loading === 0) {
              return (
                <span
                  className={clsx(
                    "px-2 truncate flex items-center justify-center",
                    isExtraSmall ? "text-sm" : "text-base",
                    row.className ?? "",
                  )}
                >
                  {row.avgWeeklyMovement !== undefined ? transformQuantity(row.avgWeeklyMovement, 1) : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
        {
          id: "daysOfSupply",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate flex items-center justify-center"
              title="DOS"
            >
              DOS
            </div>
          ),
          value: (row: IData) => {
            if (row.daysOfSupply || loading === 0) {
              return (
                <span
                  className={clsx(
                    "px-2 truncate flex items-center justify-center",
                    isExtraSmall ? "text-sm" : "text-base",
                    row.className ?? "",
                  )}
                >
                  {row.daysOfSupply !== undefined ? transformQuantity(row.daysOfSupply, 1) : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
      ]
    : [
        {
          id: "doh",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate flex items-center justify-center"
              title="DOH"
            >
              DOH
            </div>
          ),
          value: (row: IData) => {
            if (row.doh || loading === 0) {
              return (
                <span
                  className={clsx(
                    "px-2 truncate flex items-center justify-center",
                    isExtraSmall ? "text-sm" : "text-base",
                    row.className ?? "",
                  )}
                >
                  {row.doh !== undefined ? transformQuantity(row.doh, 1) : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
        {
          id: "its",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate flex items-center justify-center"
              title="ITS"
            >
              ITS
            </div>
          ),
          value: (row: IData) => {
            if (row.its || loading === 0) {
              return (
                <span
                  className={clsx(
                    "px-2 truncate flex items-center justify-center",
                    isExtraSmall ? "text-sm" : "text-base",
                    row.className ?? "",
                  )}
                >
                  {row.its !== undefined ? transformQuantity(row.its, 1) : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
      ]),
];

export const getTotal = (
  row: IData<number>,
  isUnified: boolean,
  isFar: boolean,
  data?: {
    isStoreFilterApplied: boolean;
    totalWarehouseData?: IUnitsTotalData;
    totalStoreData?: IUnitsTotalData;
    column: "its" | "doh" | "avgWeeklyMovement" | "daysOfSupply";
    tab: UnitTab;
  },
) => {
  if (
    row.dcInventory === undefined ||
    row.inTransitToDC === undefined ||
    row.onOrder === undefined ||
    row.inTransitToStore === undefined ||
    ((isUnified || isFar) && row.storeInventory === undefined) ||
    (isUnified && row.dsdOnOrder === undefined)
  ) {
    return undefined;
  }

  const total =
    row.dcInventory +
    row.inTransitToDC +
    row.onOrder +
    row.inTransitToStore +
    (isUnified || isFar ? row.storeInventory : 0) +
    (isUnified ? row.dsdOnOrder : 0);

  /* istanbul ignore next */
  if (data) {
    const demandData = data.isStoreFilterApplied
      ? data.totalStoreData
      : data.totalWarehouseData;

    if (demandData) {
      const key = unitTabs[data.tab].label.replace(" ", "");
      if (data.column === "its") {
        const demand =
          demandData[`fourWeekFrwdDemand${key}` as keyof IUnitsTotalData] || 0;
        if (!demand) {
          return undefined;
        }
        return (total / demand) * 28;
      } else if (data.column === "doh") {
        const demand =
          demandData[`eightWeekHstrSeln${key}` as keyof IUnitsTotalData] || 0;
        if (!demand) {
          return undefined;
        }
        return (total / demand) * 56;
      } else if (data.column === "avgWeeklyMovement") {
        const demand =
          demandData[`twentySixWeekHstrSales${key}` as keyof IUnitsTotalData] || 0;
        if (demand === undefined) {
          return undefined;
        }
        return demand / 26;
      } else if (data.column === "daysOfSupply") {
        const demand =
          demandData[`twentySixWeekHstrSales${key}` as keyof IUnitsTotalData] || 0;
        if (!demand) {
          return undefined;
        }
        return (total / demand) * 182;
      }
    }
  }
  return total;
};

/* istanbul ignore next */
const getValues = (
  row: IUnitsData,
  fieldName: keyof IUnits,
  isUnified: boolean,
  isFar: boolean,
) => {
  const response: IData<number> = {};
  if (!row) {
    return response;
  }
  Object.entries(row).forEach(([key, value]) => {
    if (isUnified || isFar || key !== "storeInventory") {
      if (value && value[fieldName] !== undefined) {
        response[key] = value[fieldName];
      }
    }
  });
  return response;
};

export const getDsdTotal = (
  row: IData<number>,
  data?: {
    isStoreFilterApplied: boolean;
    totalWarehouseData?: IUnitsTotalData;
    totalStoreData?: IUnitsTotalData;
    column: "its" | "doh" | "avgWeeklyMovement" | "daysOfSupply";
    tab: UnitTab;
  },
) => {
  if (row.dsdOnOrder === undefined || row.storeInventory === undefined) {
    return undefined;
  }

  const total = row.dsdOnOrder + row.storeInventory;

  /* istanbul ignore next */
  if (data) {
    const demandData = data.isStoreFilterApplied
      ? data.totalStoreData
      : data.totalWarehouseData;

    if (demandData) {
      const key = unitTabs[data.tab].label.replace(" ", "");
      if (data.column === "its") {
        const demand =
          demandData[`fourWeekFrwdDemand${key}` as keyof IUnitsTotalData] || 0;
        if (!demand) {
          return undefined;
        }
        return (total / demand) * 28;
      } else if (data.column === "doh") {
        const demand =
          demandData[`eightWeekHstrSeln${key}` as keyof IUnitsTotalData] || 0;
        if (!demand) {
          return undefined;
        }
        return (total / demand) * 56;
      } else if (data.column === "avgWeeklyMovement") {
        const demand =
          demandData[`twentySixWeekHstrSales${key}` as keyof IUnitsTotalData] || 0;
        if (demand === undefined) {
          return undefined;
        }
        return demand / 26;
      } else if (data.column === "daysOfSupply") {
        const demand =
          demandData[`twentySixWeekHstrSales${key}` as keyof IUnitsTotalData] || 0;
        if (!demand) {
          return undefined;
        }
        return (total / demand) * 182;
      }
    }
  }
  return total;
};

/* istanbul ignore next */
const getDsdValues = (row: IUnitsData, fieldName: keyof IUnits) => {
  const response: IData<number> = {};
  if (!row) {
    return response;
  }
  Object.entries(row).forEach(([key, value]) => {
    if (key !== "inTransitToStore") {
      if (value && value[fieldName] !== undefined) {
        response[key] = value[fieldName];
      }
    }
  });
  return response;
};

export const getReportRows = ({
  isSmall,
  isDsd,
  isFar,
  isRetailOps,
  isUnified,
  unitsData,
  isStoreFilterApplied,
  totalWarehouseData,
  totalStoreData,
  selectedUnitTab,
  isUnitsDataLoading,
}: {
  isSmall: boolean;
  isDsd: boolean;
  isFar: boolean;
  isRetailOps?: boolean;
  isUnified: boolean;
  unitsData: IUnitsData;
  isStoreFilterApplied: boolean;
  totalWarehouseData: IUnitsTotalData;
  totalStoreData: IUnitsTotalData;
  selectedUnitTab: UnitTab;
  isUnitsDataLoading: number;
}) => {
  if (isSmall) {
    if (isDsd) {
      return [
        {
          id: 0,
          label: "Store Inventory",
          units: unitsData.storeInventory?.unitCount,
          doh: unitsData.storeInventory?.daysOfSOH,
          its: unitsData.storeInventory?.itemTimeSupply,
          avgWeeklyMovement: unitsData.storeInventory?.avgWeeklyMovement,
          daysOfSupply: unitsData.storeInventory?.daysOfSupply,
        },
        {
          id: 1,
          label: "In Transit to Store",
        },
        {
          id: 2,
          label: "DSD Order",
          units: unitsData.dsdOnOrder?.unitCount,
          doh: unitsData.dsdOnOrder?.daysOfSOH,
          its: unitsData.dsdOnOrder?.itemTimeSupply,
          avgWeeklyMovement: unitsData.dsdOnOrder?.avgWeeklyMovement,
          daysOfSupply: unitsData.dsdOnOrder?.daysOfSupply,
        },
        {
          id: 3,
          label: "Total",
          units: getDsdTotal(getDsdValues(unitsData, "unitCount")),
          doh: getDsdTotal(getDsdValues(unitsData, "unitCount"), {
            isStoreFilterApplied,
            totalWarehouseData,
            totalStoreData,
            column: "doh",
            tab: selectedUnitTab,
          }),
          its: getDsdTotal(getDsdValues(unitsData, "unitCount"), {
            isStoreFilterApplied,
            totalWarehouseData,
            totalStoreData,
            column: "its",
            tab: selectedUnitTab,
          }),
          avgWeeklyMovement: getDsdTotal(getDsdValues(unitsData, "unitCount"), {
            isStoreFilterApplied,
            totalWarehouseData,
            totalStoreData,
            column: "avgWeeklyMovement",
            tab: selectedUnitTab,
          }),
          daysOfSupply: getDsdTotal(getDsdValues(unitsData, "unitCount"), {
            isStoreFilterApplied,
            totalWarehouseData,
            totalStoreData,
            column: "daysOfSupply",
            tab: selectedUnitTab,
          }),
          className: "font-semibold",
        },
      ];
    }
    return [
      {
        id: 0,
        label: "Store Inventory",
        units: unitsData.storeInventory?.unitCount,
        doh: unitsData.storeInventory?.daysOfSOH,
        its: unitsData.storeInventory?.itemTimeSupply,
        avgWeeklyMovement: unitsData.storeInventory?.avgWeeklyMovement,
        daysOfSupply: unitsData.storeInventory?.daysOfSupply,
      },
      {
        id: 1,
        label: "In Transit to Store",
        units: unitsData.inTransitToStore?.unitCount,
        doh: unitsData.inTransitToStore?.daysOfSOH,
        its: unitsData.inTransitToStore?.itemTimeSupply,
        avgWeeklyMovement: unitsData.inTransitToStore?.avgWeeklyMovement,
        daysOfSupply: unitsData.inTransitToStore?.daysOfSupply,
      },
      ...(isUnified
        ? [
            {
              id: 2,
              label: "DSD Order",
              units: unitsData.dsdOnOrder?.unitCount,
              doh: unitsData.dsdOnOrder?.daysOfSOH,
              its: unitsData.dsdOnOrder?.itemTimeSupply,
              avgWeeklyMovement: unitsData.dsdOnOrder?.avgWeeklyMovement,
              daysOfSupply: unitsData.dsdOnOrder?.daysOfSupply,
            },
          ]
        : []),
      {
        id: 3,
        label: "DC Inventory",
        units: unitsData.dcInventory?.unitCount,
        doh: unitsData.dcInventory?.daysOfSOH,
        its: unitsData.dcInventory?.itemTimeSupply,
        avgWeeklyMovement: unitsData.dcInventory?.avgWeeklyMovement,
        daysOfSupply: unitsData.dcInventory?.daysOfSupply,
      },
      {
        id: 4,
        label: "In Transit to DC",
        units: unitsData.inTransitToDC?.unitCount,
        doh: unitsData.inTransitToDC?.daysOfSOH,
        its: unitsData.inTransitToDC?.itemTimeSupply,
        avgWeeklyMovement: unitsData.inTransitToDC?.avgWeeklyMovement,
        daysOfSupply: unitsData.inTransitToDC?.daysOfSupply,
      },
      {
        id: 5,
        label: "Vendor On Order (WHS)",
        units: unitsData.onOrder?.unitCount,
        doh: unitsData.onOrder?.daysOfSOH,
        its: unitsData.onOrder?.itemTimeSupply,
        avgWeeklyMovement: unitsData.onOrder?.avgWeeklyMovement,
        daysOfSupply: unitsData.onOrder?.daysOfSupply,
      },
      {
        id: 6,
        label: "Total",
        units: getTotal(
          getValues(unitsData, "unitCount", isUnified, isFar),
          isUnified,
          isFar,
        ),
        doh: getTotal(
          getValues(unitsData, "unitCount", isUnified, isFar),
          isUnified,
          isFar,
          {
            isStoreFilterApplied,
            totalWarehouseData,
            totalStoreData,
            column: "doh",
            tab: selectedUnitTab,
          },
        ),
        its: getTotal(
          getValues(unitsData, "unitCount", isUnified, isFar),
          isUnified,
          isFar,
          {
            isStoreFilterApplied,
            totalWarehouseData,
            totalStoreData,
            column: "its",
            tab: selectedUnitTab,
          },
        ),
        avgWeeklyMovement: getTotal(
          getValues(unitsData, "unitCount", isUnified, isFar),
          isUnified,
          isFar,
          {
            isStoreFilterApplied,
            totalWarehouseData,
            totalStoreData,
            column: "avgWeeklyMovement",
            tab: selectedUnitTab,
          }
        ),
        daysOfSupply: getTotal(
          getValues(unitsData, "unitCount", isUnified, isFar),
          isUnified,
          isFar,
          {
            isStoreFilterApplied,
            totalWarehouseData,
            totalStoreData,
            column: "daysOfSupply",
            tab: selectedUnitTab,
          }
        ),
        className: "font-semibold",
      },
    ];
  }
  if (isDsd) {
    return [
      {
        id: 0,
        label: unitTabs[selectedUnitTab].label,
        ...getDsdValues(unitsData, "unitCount"),
        total: getDsdTotal(getDsdValues(unitsData, "unitCount")),
        isLoading: isUnitsDataLoading,
      },
      {
        id: 1,
        label: isRetailOps ? "Average Weekly Movement" : "Day of Sales on Hand",
        ...getDsdValues(unitsData, isRetailOps ? "avgWeeklyMovement" : "daysOfSOH"),
        total: getDsdTotal(getDsdValues(unitsData, "unitCount"), {
          isStoreFilterApplied,
          totalWarehouseData,
          totalStoreData,
          column: isRetailOps ? "avgWeeklyMovement" : "doh",
          tab: selectedUnitTab,
        }),
        isLoading: isUnitsDataLoading,
      },
      {
        id: 2,
        label: isRetailOps ? "Days of Supply" : "Item Time Supply",
        ...getDsdValues(unitsData, isRetailOps ? "daysOfSupply" : "itemTimeSupply"),
        total: getDsdTotal(getDsdValues(unitsData, "unitCount"), {
          isStoreFilterApplied,
          totalWarehouseData,
          totalStoreData,
          column: isRetailOps ? "daysOfSupply" : "its",
          tab: selectedUnitTab,
        }),
        isLoading: isUnitsDataLoading,
      },
    ];
  }
  return [
    {
      id: 0,
      label: unitTabs[selectedUnitTab].label,
      ...getValues(unitsData, "unitCount", isUnified, isFar),
      total: getTotal(
        getValues(unitsData, "unitCount", isUnified, isFar),
        isUnified,
        isFar,
      ),
      isLoading: isUnitsDataLoading,
    },
    {
      id: 1,
      label: isRetailOps ? "Average Weekly Movement" : "Day of Sales on Hand",
      ...getValues(unitsData, isRetailOps ? "avgWeeklyMovement" : "daysOfSOH", isUnified, isFar),
      total: getTotal(
        getValues(unitsData, "unitCount", isUnified, isFar),
        isUnified,
        isFar,
        {
          isStoreFilterApplied,
          totalWarehouseData,
          totalStoreData,
          column: isRetailOps ? "avgWeeklyMovement" : "doh",
          tab: selectedUnitTab,
        },
      ),
      isLoading: isUnitsDataLoading,
    },
    {
      id: 2,
      label: isRetailOps ? "Days of Supply" : "Item Time Supply",
      ...getValues(unitsData, isRetailOps ? "daysOfSupply" : "itemTimeSupply", isUnified, isFar),
      total: getTotal(
        getValues(unitsData, "unitCount", isUnified, isFar),
        isUnified,
        isFar,
        {
          isStoreFilterApplied,
          totalWarehouseData,
          totalStoreData,
          column: isRetailOps ? "daysOfSupply" : "its",
          tab: selectedUnitTab,
        },
      ),
      isLoading: isUnitsDataLoading,
    },
  ];
};
